/** @format */

import { DateTime } from 'luxon/src/datetime';
import { NgZone } from '@angular/core';
import { TimeService } from '@pdp/utils';
import { EventBusService, Events, EmitEvent } from '@shared/data-access/services';

export class Timer {
	public startDate: string;
	public endDate: string;
	private ngZone: NgZone;

	public seconds = 0;
	public minutes = 0;
	public hours = 0;
	public days = 0;

	public timer;

	constructor(
		startDate: string,
		endDate: string,
		ngZone: NgZone,
		private eventBusService: EventBusService,
		private timeService: TimeService
	) {
		this.startDate = startDate;
		this.endDate = endDate;
		this.ngZone = ngZone;
	}

	/**
	 * Starts the timer
	 */
	public startTimer() {
		this.countDownTimer(this.endDate);
	}

	/**
	 * Counts down the time based of the end date
	 * will fire event when the timer ends
	 * Event fired:
	 * Events.TimerEnded
	 * @param endDate
	 */
	private countDownTimer(endDate: string) {
		this.ngZone.runOutsideAngular(() => {
			this.timer = setInterval(() => {
				// Changes here will not propagate into your view.
				this.ngZone.run(() => {
					const now = this.timeService.getCurrentTime();
					// adjust for timezone
					const copy = this.fetchAdjustTime(endDate);
					const distance = this.findDistance(copy, now);

					if (distance <= 0) {
						clearInterval(this.timer);
						this.timer = null;
						this.emitEvent(Events.TimerEnded);
						return;
					}

					this.updateTime(distance);
				});
			}, 1000);
		});
	}

	/**
	 * Gets time for current timezone
	 * @param date
	 */
	public fetchAdjustTime(date: string) {
		// Considering the date is in local time "America/New_York"
		return this.timeService.parseDateFromISO(date, { keepLocalTime: true });
	}

	/**
	 * Emits the event to subscribers
	 * @param event
	 */
	public emitEvent(event: Events) {
		this.eventBusService.emit(new EmitEvent(event));
	}

	/**
	 * Finds the distance between 2 dates
	 * @param time1
	 * @param time2
	 */
	public findDistance(time1: DateTime, time2: DateTime) {
		return this.timeService.getDistanceBetweenDates(time1, time2);
	}

	/**
	 * Updates the days, hours, min, sec for the distance
	 * @param distance
	 */
	public updateTime(distance) {
		const second = 1000;
		const minute = second * 60;
		const hour = minute * 60;
		const day = hour * 24;

		this.days = Math.floor(distance / day);
		this.hours = Math.floor((distance % day) / hour);
		this.minutes = Math.floor((distance % hour) / minute);
		this.seconds = Math.floor((distance % minute) / second);
	}
}
